import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { sustainPillars } from '@/mocks/sustainability';

const eyebrow = 'text-[14px] tracking-[0.25em] uppercase text-primary-600 mb-3';
const h2 = 'font-heading text-3xl md:text-5xl leading-tight text-foreground-950 mb-5';
const btnOutline = 'inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-foreground-900 text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer hover:bg-foreground-950 hover:text-background-50 transition-colors';
const btnGradient = 'group inline-flex rounded-full bg-gradient-to-br from-primary-300 via-primary-500 to-accent-400 p-px whitespace-nowrap cursor-pointer';
const btnGradientInner = 'inline-flex items-center gap-2 px-7 py-3 rounded-full bg-background-50 text-foreground-950 text-xs tracking-widest uppercase transition-colors group-hover:bg-gradient-to-br group-hover:from-primary-500 group-hover:via-primary-600 group-hover:to-accent-500 group-hover:text-background-50';

type Labeled = { title: string; desc: string };

export function SustainabilityHero() {
  const { t } = useTranslation();
  return (
    <section className="relative h-[420px] md:h-[520px] w-full overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20a%20gently%20meandering%20river%20curving%20through%20vast%20green%20rice%20paddy%20fields%2C%20geometric%20patchwork%20of%20lush%20emerald%20paddies%2C%20distant%20green%20mountains%20along%20the%20horizon%2C%20a%20few%20small%20farmhouses%20beside%20the%20river%2C%20soft%20morning%20light%2C%20fresh%20vivid%20green%20palette%2C%20wide%20panoramic%20landscape%20photography%2C%20no%20palm%20or%20coconut%20trees&width=1920&height=700&seq=lam-sustain-hero-3&orientation=landscape&nocache=true"
        alt="Mekong river winding through the delta under a blue sky"
        title="Sustainability at EverRice"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-background-50/88 via-background-50/55 to-transparent" />
      <div className="relative h-full flex items-center page-gutter">
        <div className="max-w-xl fade-up pt-24 md:pt-28 text-shadow-soft">
          <div className="rounded-2xl border border-background-50/60 bg-background-50/55 p-6 md:p-10">
            <p className="font-heading text-lg tracking-[0.3em] text-foreground-700 mb-4">{t('sustainabilityPage.hero.eyebrow')}</p>
            <h1 className="font-heading text-4xl md:text-[2.75rem] leading-[1.1] tracking-[0.03em] text-foreground-950 mb-6">{t('sustainabilityPage.hero.title')}</h1>
            <p className="text-sm md:text-base text-foreground-800 leading-relaxed">{t('sustainabilityPage.hero.desc')}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function SustainabilityIntro() {
  const { t } = useTranslation();
  return (
    <section className="bg-background-50 page-gutter py-16 md:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className={eyebrow}>{t('sustainabilityPage.intro.eyebrow')}</p>
          <h2 className={h2}>{t('sustainabilityPage.intro.title')}</h2>
          <div className="space-y-4 text-sm text-foreground-700 leading-relaxed max-w-lg">
            <p>{t('sustainabilityPage.intro.p1')}</p>
            <p>{t('sustainabilityPage.intro.p2')}</p>
          </div>
          <div className="flex flex-wrap gap-4 mt-8">
            <Link to="/quality" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer">{t('sustainabilityPage.intro.ctaQuality')} <i className="ri-arrow-right-line" /></Link>
            <Link to="/contact" className={btnOutline}>{t('sustainabilityPage.intro.ctaWork')} <i className="ri-mail-line" /></Link>
          </div>
        </div>
        <div className="w-full h-72 md:h-[440px] rounded-lg overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Close-up%20of%20a%20farmer%20hands%20carefully%20holding%20young%20green%20rice%20seedlings%20above%20rich%20soil%2C%20soft%20warm%20daylight%2C%20fresh%20airy%20pastel%20green%20tones%2C%20warm%20documentary%20photography&width=900&height=800&seq=lam-sustain-planting&orientation=portrait"
            alt="Hands planting rice seedlings"
            title="Sustainable rice farming at EverRice"
            className="w-full h-full object-cover object-top"
          />
        </div>
      </div>
    </section>
  );
}

export function SustainabilityPillars() {
  const { t } = useTranslation();
  const items = t('sustainabilityPage.pillars.items', { returnObjects: true }) as unknown as Labeled[];
  return (
    <section className="bg-background-100 page-gutter py-16 md:py-24">
      <div className="text-center mb-12">
        <p className={eyebrow}>{t('sustainabilityPage.pillars.eyebrow')}</p>
        <h2 className={`${h2} mb-0`}>{t('sustainabilityPage.pillars.title')}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {items.map((p, i) => (
          <article key={p.title} className={`rounded-lg overflow-hidden flex flex-col ${i === 1 ? 'bg-primary-800 text-background-50' : 'bg-background-50 border border-background-200'}`}>
            <div className="w-full h-52 overflow-hidden">
              <img src={sustainPillars[i].img} alt={p.title} title={`${p.title} - EverRice`} className="w-full h-full object-cover object-top" />
            </div>
            <div className="p-6">
              <h3 className="font-heading text-2xl mb-2">{p.title}</h3>
              <p className={`text-sm leading-relaxed ${i === 1 ? 'text-background-200' : 'text-foreground-600'}`}>{p.desc}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export function SustainabilityCta() {
  const { t } = useTranslation();
  return (
    <section className="bg-background-50 page-gutter py-16 md:py-24">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6 rounded-lg bg-gradient-to-br from-primary-500 via-primary-700 to-primary-900 text-background-50 px-6 md:px-14 py-10">
        <div>
          <h2 className="font-heading text-2xl md:text-4xl mb-3">{t('sustainabilityPage.cta.title')}</h2>
          <p className="text-sm opacity-90 max-w-xl leading-relaxed">{t('sustainabilityPage.cta.desc')}</p>
        </div>
        <div className="flex flex-wrap gap-3 shrink-0">
          <Link to="/contact#send-message" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-background-50 text-foreground-950 text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors">{t('sustainabilityPage.cta.ctaContact')} <i className="ri-arrow-right-line" /></Link>
          <Link to="/products" className={btnGradient}>
            <span className={btnGradientInner}>{t('sustainabilityPage.cta.ctaProducts')} <i className="ri-leaf-line" /></span>
          </Link>
        </div>
      </div>
    </section>
  );
}